import * as Actions from '../redux/actions/actions-consts';
import {
  setWebsiteCreatedCookie,
  trackPageview,
  trackEvent,
  trackErrorOnAction,
  trackDWH,
  trackToBunchBox,
  loadTrackingIframe,
  trackGtmEvent,
  TrackedSignupActionName,
  TrackingLabel,
} from '../modules/tracking';
import { langToLocale } from '../modules/helpers';
import { isGhostInspectorTest } from '../modules/cookies';
import { Middleware } from 'redux';
import { AppState } from '../redux/reducers';
import { TRACKING_IFRAME_ENDPOINT } from '../modules/constants';
import isAction from './helpers';
import { getSelectedTrackingState } from '../redux/selectors';
import {
  ROUTER_PUSH_ACTION,
  SET_INDUSTRY,
  SET_PACKAGE,
  SET_PRODUCT,
  SET_SUBTYPE,
  SET_SIGNUP_WITH_PRODUCT_NAMING,
} from '../redux/actions/actions-consts';

export const SIGNUP_FLOW_NAMESPACE = 'signup-flow';
const FREE_SIGNUP_GOAL_PATHNAME = '/free-signup';

const nextTick = require('next-tick');

export interface TrackingOptions {
  tracked?: {
    action: TrackedSignupActionName;
    label: TrackingLabel;
  };
}

interface TrackedAction {
  type: string;
  // tslint:disable-next-line no-any
  payload?: any;
  error?: boolean;
  meta?: TrackingOptions;
}

type TrackingState = ReturnType<typeof getSelectedTrackingState>;

const CUSTOM_DIMENSIONS = new Map<string, string>([
  [SET_INDUSTRY, 'industry'],
  [SET_PRODUCT, 'toolType'],
  [SET_PACKAGE, 'productType'],
  [SET_SUBTYPE, 'style'],
]);

const isFinalAction = (type: string): boolean =>
  type === Actions.WEBSITE_CREATION_SUCCESS || type === Actions.CHECKOUT_CONFIG_GENERATED;

const trackTaggedAction = (action: TrackedAction, state: AppState): void => {
  if (action.error) {
    trackErrorOnAction(action.type, action.payload);
  } else {
    trackEvent(action.type, action.payload);
  }
  // here we use the raw data as it is our data warehouse
  const trackingState = isFinalAction(action.type) ? getSelectedTrackingState(state) : undefined;
  trackDWH(action.meta.tracked.action, action.meta.tracked.label, {
    payload: action.payload,
    trackingState,
  });
};

const trackRouterPush = (pathname: string, state: AppState): void => {
  const { lang } = state.options;
  trackToBunchBox('evaluate experiments', {
    attributes: {
      language: lang,
    },
  });
  trackGtmEvent('virtpath', {
    virtpath: window.location.pathname,
  });
  trackPageview(pathname);
};

const trackPayload = (action: TrackedAction, state: AppState): void => {
  if (!action.payload) {
    return;
  }

  if (action.type === ROUTER_PUSH_ACTION) {
    trackRouterPush(action.payload, state);
  }

  const dimension = CUSTOM_DIMENSIONS.get(action.type);
  if (dimension) {
    trackGtmEvent('Custom Dimensions', {
      [dimension]: action.payload,
    });
  }

  if (action.type === SET_SIGNUP_WITH_PRODUCT_NAMING) {
    const experiment = action.payload;
    trackToBunchBox('evaluate experiments', {
      attributes: {
        experiment,
      },
    });
  }
};

const trackWebsiteCreated = (
  action: TrackedAction,
  state: AppState,
  trackingState: TrackingState
): void => {
  trackToBunchBox('track event goal', 'website_created_with_signup_flow');

  setWebsiteCreatedCookie('website_created_with_signup_flow');
  const locale = langToLocale(state.options.lang);
  loadTrackingIframe(TRACKING_IFRAME_ENDPOINT, locale);
  trackPageview(FREE_SIGNUP_GOAL_PATHNAME);

  trackEvent(`FINAL_${action.type}`, trackingState);
  trackGtmEvent('freeCreatorWebsiteCreated', {
    website: action.payload?.username,
  });
};

const trackFinalAction = (action: TrackedAction, state: AppState): void => {
  const trackingState = getSelectedTrackingState(state);

  if (action.type === Actions.WEBSITE_CREATION_SUCCESS) {
    trackWebsiteCreated(action, state, trackingState);
  }

  if (action.type === Actions.CHECKOUT_CONFIG_GENERATED) {
    trackEvent(`FINAL_${action.type}`, trackingState);
  }
};

// tslint:disable-next-line no-any
const trackAction = (action: any, state: AppState): void => {
  if (isGhostInspectorTest()) {
    console.log('is GhostInspector Test ..'); // eslint-disable-line no-console
    return;
  }

  if (!isAction(action)) {
    return;
  }

  if (action.meta?.tracked) {
    trackTaggedAction(action, state);
  }

  trackPayload(action, state);

  if (isFinalAction(action.type)) {
    trackFinalAction(action, state);
  }
};

const trackingMiddleware: Middleware<{}, AppState> = (store) => {
  return (next) => {
    // tslint:disable-next-line no-any
    return (action: any) => {
      const state = store.getState();

      nextTick(() => trackAction(action, state));

      next(action);
    };
  };
};

export default trackingMiddleware;
